import type { Runtime } from '@betool/identity-runtime'

/**
 * La SURVEILLANCE du slot : ce qui rend la déconnexion mutuelle ENTRE
 * APPLICATIONS.
 *
 * POURQUOI LE SIGNAL NE SUFFIT PAS
 * ---------------------------------------------------------------------------
 * Le runtime diffuse les pertes d'identité par un `BroadcastChannel`, et un
 * `BroadcastChannel` est limité à UNE ORIGINE. Il propage donc entre onglets
 * de beTool Sign, ou entre onglets du kiosque — jamais de l'un vers l'autre.
 * Se déconnecter au kiosque n'émet rien qui puisse atteindre cette page.
 *
 * Et il y a plus profond : une fois ouverte, la session de cette application
 * est AUTONOME. Son jeton vit sa propre durée et ne redemande jamais si
 * l'identité qui l'a autorisée existe encore. Mesuré : déconnexion au kiosque,
 * retour sur l'onglet de Sign, navigation normale.
 *
 * C'est la règle du dépôt sur tout accès authentifié une seule fois : il faut
 * les DEUX — la révocation poussée (immédiate, mais limitée à l'origine qui
 * l'émet) ET le balayage périodique, seule protection quand le retrait vient
 * d'ailleurs.
 *
 * CE QU'ON INTERROGE, ET POURQUOI C'EST LUI
 * ---------------------------------------------------------------------------
 * Le BFF du tenant : il tient le trousseau, et c'est la seule source que les
 * deux applications partagent. Le slot n'y est plus ⇒ l'identité est partie,
 * quelle que soit l'application qui l'a retirée.
 */

/**
 * Délai MINIMAL entre deux contrôles — un garde-fou ANTI-RAFALE, pas une
 * période de sondage.
 *
 * Il a valu cinq minutes, et c'était faux. Le contrôle fait au chargement
 * consommait la fenêtre : quelqu'un qui ouvre cette page, va se déconnecter
 * ailleurs et revient dans les cinq minutes n'était pas déconnecté — c'est-à-
 * dire le cas NORMAL. Mesuré en production par le banc croisé : aucun appel au
 * gardien, et une session qui survit à son retrait du trousseau.
 *
 * Dix secondes suffisent à ce qu'on voulait vraiment éviter : un va-et-vient
 * rapide entre deux onglets qui déclencherait une rafale. Le gardien plafonne à
 * 60 requêtes par minute, toutes routes confondues et ses propres fichiers
 * compris ; six contrôles par minute et par onglet, au pire, lui laissent de
 * quoi se servir lui-même.
 *
 * Et toujours PAS de tic de fond : le contrôle se déclenche au RETOUR sur
 * l'onglet, qui est le geste réel — on se déconnecte ailleurs, on revient ici.
 */
const DELAI_MINIMAL_MS = 10_000

export interface OptionsSurveillance {
  /** Coupe la session locale — sans appel serveur, le jeton ne vaut plus. */
  fermer: () => void
  /** Vrai tant que l'application a une session à protéger. */
  estConnecte: () => boolean
  /** Injecté par les tests ; `DELAI_MINIMAL_MS` en production. */
  periodeMs?: number
}

/**
 * Surveille le slot de cet onglet, et rend la fonction d'arrêt.
 *
 * UN SEUL DÉCLENCHEUR, et c'est le geste réel : le RETOUR sur l'onglet. On se
 * déconnecte ailleurs, on revient ici, et on doit trouver la porte fermée.
 *
 * Pas de tic de fond : un onglet où personne ne touche à rien n'a rien à
 * vérifier, et le BFF partage son quota de requêtes avec les fichiers
 * statiques qu'il sert. Le contrôle d'ouverture couvre le cas de l'onglet resté
 * au premier plan pendant une déconnexion ailleurs — au retour du suivant.
 */
export function surveillerSlot(runtime: Runtime, options: OptionsSurveillance): () => void {
  const delaiMinimal = options.periodeMs ?? DELAI_MINIMAL_MS
  let enCours = false
  let arrete = false
  let dernierControle = 0

  async function verifier(): Promise<void> {
    // Une vérification à la fois, et pas plus d'une par fenêtre : un
    // va-et-vient rapide entre deux onglets déclencherait autrement une rafale
    // d'appels sur un service dont le quota est partagé avec ses assets.
    if (enCours || arrete || !options.estConnecte()) return
    if (Date.now() - dernierControle < delaiMinimal) return

    const slot = runtime.currentSession()
    if (slot === null) return

    enCours = true
    dernierControle = Date.now()
    try {
      const vivants = await runtime.listBrowserSessions()
      if (arrete) return

      const encoreLa = vivants.find((session) => session.slotRef === slot.slotRef)
      // ABSENT du trousseau, ou présent mais mort : dans les deux cas
      // l'identité ne porte plus cette session. Un slot « expired » laissé
      // ouvert redonnerait le droit de travailler à quelqu'un que la
      // plateforme a déconnecté.
      if (encoreLa === undefined || encoreLa.state !== 'live') options.fermer()
    } catch {
      // Panne ou BFF injoignable : on NE FERME PAS. Déconnecter sur une panne
      // réseau ferait perdre son travail à quelqu'un dont l'identité est
      // parfaitement valide — et la vérification sera refaite au tic suivant.
    } finally {
      enCours = false
    }
  }

  function surVisibilite(): void {
    if (document.visibilityState === 'visible') void verifier()
  }

  document.addEventListener('visibilitychange', surVisibilite)

  // Un premier passage tout de suite : l'onglet a pu rester ouvert pendant que
  // la déconnexion se faisait ailleurs, et son premier rendu ne doit pas
  // afficher des écrans qu'on n'a plus le droit de voir.
  void verifier()

  return () => {
    arrete = true
    document.removeEventListener('visibilitychange', surVisibilite)
  }
}
